import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuth } from '@/contexts/auth'
import { invalidateLibraryQueries } from '@/hooks/books/useBooks'
import { captureError } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'
import type { BookLoan, ClubPeer } from '@/types'

/**
 * Loans where the user is lender or borrower.
 * Keyed under ['books', userId] so book mutations refresh it too.
 */
export function useMyLoans() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['books', user?.id, 'loans'],
    queryFn: async (): Promise<BookLoan[]> => {
      const { data, error } = await supabase.rpc('get_my_loans')
      if (error) {
        captureError(error, { context: 'useMyLoans' })
        throw error
      }
      return data || []
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000,
  })
}

/** Active loan (not returned) touching a book, from either side */
export function useActiveLoanForBook(bookId: string) {
  const { data: loans } = useMyLoans()
  return useMemo(
    () =>
      loans?.find(
        (loan) => !loan.returned_on && (loan.book_id === bookId || loan.borrower_book_id === bookId)
      ) ?? null,
    [loans, bookId]
  )
}

export function useClubPeers() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['club-peers', user?.id],
    queryFn: async (): Promise<ClubPeer[]> => {
      const { data, error } = await supabase.rpc('get_club_peers')
      if (error) {
        captureError(error, { context: 'useClubPeers' })
        throw error
      }
      return data || []
    },
    enabled: !!user?.id,
  })
}

export function useLendBook() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (vars: {
      bookId: string
      borrowerId?: string | null
      borrowerName?: string | null
      lentOn?: string
    }) => {
      const { data, error } = await supabase.rpc('lend_book', {
        p_book_id: vars.bookId,
        p_borrower_id: vars.borrowerId ?? null,
        p_borrower_name: vars.borrowerName ?? null,
        ...(vars.lentOn && { p_lent_on: vars.lentOn }),
      })
      if (error) {
        captureError(error, { context: 'useLendBook', bookId: vars.bookId })
        throw error
      }
      return data as string
    },
    onSuccess: () => invalidateLibraryQueries(queryClient, user?.id),
  })
}

export function useReturnLoan() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (loan: BookLoan) => {
      const { error } = await supabase.rpc('return_book_loan', { p_loan_id: loan.id })
      if (error) {
        captureError(error, { context: 'useReturnLoan', loanId: loan.id })
        throw error
      }
    },
    onSuccess: () => invalidateLibraryQueries(queryClient, user?.id),
  })
}
